import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CorDaEquipe, EmploymentTerms, LimitesOperacao, LimitesPorPessoa, Papel, Profile,
} from '@core/types';
import Layout from '../componentes/Layout';
import Carregando from '../componentes/Carregando';
import Aviso from '../componentes/Aviso';
import {
  alternarDesligamento, criarFuncionario, definirLimitesDaPessoa, excluirFuncionario,
  liberacoesMeuDinheiro, liberarMeuDinheiro,
  limitesDaEquipe, limitesDaOperacao, perfis, redefinirSenha,
  salvarLimitesDaLoja, salvarPerfil, salvarTermos, sincronizarMeuDinheiro, termosDaEquipe,
} from '../lib/consultas';
import { brl, dataBR, hojeISO, paraNumero } from '../lib/formato';
import { CORES_DA_EQUIPE, CORES_DISPONIVEIS, corDe } from '../lib/notas';
import { mensagemDeErro } from '../lib/supabase';
import {
  CHAVE_LIBERACOES_MEU_DINHEIRO, podeAdministrar, podeAdministrarPapel, useSessao,
} from '../lib/sessao';

// Tela 10 — Equipe. Dono e gerente chegam aqui.
//
// É o único lugar onde uma conta nasce. A senha vai direto para a Edge Function
// `equipe-admin`, que roda no servidor com service_role — o navegador nunca
// carrega chave privilegiada, e a função revalida quem chamou.
//
// HIERARQUIA (rls.sql §6 + Edge Function): você só administra quem está ABAIXO
// de você. Dono administra todos; gerente administra apenas vendedores e não
// consegue criar nem promover ninguém a gerente/dono — senão ele criaria um
// dono e entraria nele. O que esta tela faz é ESCONDER o que não é permitido;
// quem recusa de verdade é o banco e a função.
//
// Salário e comissão ficam em employment_terms (só dono), não em profiles: o
// gerente precisa ler profiles para tocar a equipe, e RLS filtra linha, não
// coluna — salário em profiles seria salário visível para o gerente.

const PAPEIS: Array<{ id: Papel; rotulo: string; resumo: string }> = [
  { id: 'vendedor', rotulo: 'Vendedor', resumo: 'Busca peça, vende e vê as próprias vendas.' },
  { id: 'gerente', rotulo: 'Gerente', resumo: '+ cadastro, entrada, caixa, relatórios, todas as vendas e cadastro de vendedor.' },
  { id: 'dono', rotulo: 'Dono', resumo: '+ folha de pagamento, custos, preços de custo, metas e equipe.' },
];

export default function Equipe() {
  const cliente = useQueryClient();
  const { perfil: eu, papel: meuPapel } = useSessao();
  const souDono = podeAdministrar(meuPapel);
  const [abrindoNovo, setAbrindoNovo] = useState(false);
  const [expandido, setExpandido] = useState<string | null>(null);

  const equipe = useQuery({ queryKey: ['perfis'], queryFn: perfis });
  const termos = useQuery({ queryKey: ['termos'], queryFn: termosDaEquipe });

  // Os tetos (069). Só o dono lê: a RLS de limites_por_pessoa nega o gerente, e
  // pedir para ele seria um erro seco na tela em vez de um bloco que não existe.
  const ajustes = useQuery({
    queryKey: ['limites-por-pessoa'],
    queryFn: limitesDaEquipe,
    enabled: souDono,
  });
  const limitesLoja = useQuery({
    queryKey: ['limites-operacao'],
    queryFn: limitesDaOperacao,
    enabled: souDono,
    staleTime: 10 * 60_000,
  });

  const porPerfil = new Map((termos.data ?? []).map((t) => [t.profile_id, t]));
  const ajustePorPerfil = new Map((ajustes.data ?? []).map((a) => [a.profile_id, a]));

  const ativos = (equipe.data ?? []).filter((p) => p.ativo);
  const inativos = (equipe.data ?? []).filter((p) => !p.ativo);

  const recarregar = () => {
    cliente.invalidateQueries({ queryKey: ['perfis'] });
    cliente.invalidateQueries({ queryKey: ['termos'] });
    cliente.invalidateQueries({ queryKey: ['limites-por-pessoa'] });
    // A tela de venda cacheia o teto EFETIVO por 10 minutos. Quem mexeu aqui
    // pode ir vender em seguida: sem isto, o balcão dele mostraria o número
    // velho até o cache vencer.
    cliente.invalidateQueries({ queryKey: ['limites-efetivos'] });
    // 094: salvar o combinado regenera a folha prevista (salvarTermos) — o
    // salário e o dia novos aparecem em Contas a pagar, no Painel e no fluxo.
    for (const chave of [['folha'], ['folha-contas'], ['fluxo']]) {
      cliente.invalidateQueries({ queryKey: chave });
    }
  };

  return (
    <Layout
      titulo="Equipe"
      acao={
        <button
          type="button"
          onClick={() => setAbrindoNovo((v) => !v)}
          className="botao-primario px-3 py-2 text-xs"
        >
          + Funcionário
        </button>
      }
    >
      <div className="space-y-4">
        {abrindoNovo && (
          <NovoFuncionario
            aoFechar={() => setAbrindoNovo(false)}
            aoCriar={() => { setAbrindoNovo(false); recarregar(); }}
          />
        )}

        {(equipe.error || termos.error) && (
          <Aviso tipo="erro">{mensagemDeErro(equipe.error ?? termos.error)}</Aviso>
        )}
        {equipe.isLoading && <Carregando texto="Carregando equipe" />}

        {ativos.length > 0 && (
          <section className="space-y-2">
            <h2 className="rotulo">Na ativa ({ativos.length})</h2>
            {ativos.map((p) => (
              <Funcionario
                key={p.id}
                perfil={p}
                termos={porPerfil.get(p.id)}
                ajuste={ajustePorPerfil.get(p.id)}
                limitesLoja={limitesLoja.data}
                equipe={equipe.data ?? []}
                souEu={p.id === eu?.id}
                aberto={expandido === p.id}
                aoAbrir={() => setExpandido(expandido === p.id ? null : p.id)}
                aoMudar={recarregar}
              />
            ))}
          </section>
        )}

        {inativos.length > 0 && (
          <section className="space-y-2">
            <h2 className="rotulo">Desligados ({inativos.length})</h2>
            {inativos.map((p) => (
              <Funcionario
                key={p.id}
                perfil={p}
                termos={porPerfil.get(p.id)}
                ajuste={ajustePorPerfil.get(p.id)}
                limitesLoja={limitesLoja.data}
                equipe={equipe.data ?? []}
                souEu={p.id === eu?.id}
                aberto={expandido === p.id}
                aoAbrir={() => setExpandido(expandido === p.id ? null : p.id)}
                aoMudar={recarregar}
              />
            ))}
          </section>
        )}

        {equipe.data && equipe.data.length === 0 && (
          <div className="cartao p-6 text-center text-sm text-mute">
            Nenhum funcionário cadastrado. Use "+ Funcionário" para criar o primeiro acesso.
          </div>
        )}

        {souDono && limitesLoja.data && <LimitesDaLoja limites={limitesLoja.data} />}

        {souDono && <MeuDinheiroQuemPodeUsar />}

        <section className="cartao p-4">
          <h2 className="rotulo">O que cada papel enxerga</h2>
          <ul className="mt-2 space-y-2 text-sm">
            {PAPEIS.map((p) => (
              <li key={p.id} className="border-l-2 border-signal pl-3">
                <span className="font-bold text-signal-soft">{p.rotulo}</span>
                <span className="block text-xs text-mute">{p.resumo}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-dim">
            Estas regras são aplicadas pelo banco (RLS), não pela tela. Mesmo acessando a API
            diretamente, um vendedor não lê o caixa nem a folha.
          </p>
        </section>
      </div>
    </Layout>
  );
}

// --- Meu dinheiro: quem pode usar (ADR-008, migração 079) ---------------------
//
// O dono NÃO vê o planejamento de ninguém, nem quem usa — decisão dele em
// 18/09/2026 ("gostaria que eu não visse nada"). O que fica com ele é um
// interruptor por papel. Ligar grava em personal_release (a RPC exige dono no
// corpo) e avisa o agente da VPS, que manda ao banco pessoal a lista de quem
// pode usar. O agente também confere sozinho a cada 5 minutos: um aviso que se
// perca só atrasa, não desfaz o botão.

const PAPEIS_DO_MEU_DINHEIRO: Array<{ papel: Papel; rotulo: string }> = [
  { papel: 'dono', rotulo: 'Donos' },
  { papel: 'gerente', rotulo: 'Gerente' },
  { papel: 'vendedor', rotulo: 'Vendedores' },
];

function MeuDinheiroQuemPodeUsar() {
  const cliente = useQueryClient();
  // A MESMA chave do menu (lib/sessao.tsx): mexeu aqui, o item aparece ou some
  // no menu deste aparelho na hora.
  const liberacoes = useQuery({ queryKey: CHAVE_LIBERACOES_MEU_DINHEIRO, queryFn: liberacoesMeuDinheiro });
  const [avisoNaoChegou, setAvisoNaoChegou] = useState<string | null>(null);

  const mudar = useMutation({
    mutationFn: async ({ papel, liberado }: { papel: Papel; liberado: boolean }) => {
      await liberarMeuDinheiro(papel, liberado);
      // O botão já mudou no banco da loja. O aviso ao agente é o que faz valer
      // NA HORA; se ele falhar, não é erro do botão — é atraso.
      try {
        await sincronizarMeuDinheiro();
        return null;
      } catch (erro) {
        return mensagemDeErro(erro);
      }
    },
    onMutate: () => setAvisoNaoChegou(null),
    onSuccess: (naoChegou) => setAvisoNaoChegou(naoChegou),
    onSettled: () => cliente.invalidateQueries({ queryKey: CHAVE_LIBERACOES_MEU_DINHEIRO }),
  });

  const ligado = (papel: Papel) =>
    liberacoes.data?.some((l) => l.papel === papel && l.liberado) ?? false;

  return (
    <section className="cartao p-4">
      <h2 className="rotulo">Meu dinheiro · quem pode usar</h2>

      {liberacoes.isLoading && <Carregando texto="Carregando" />}
      {liberacoes.error ? <Aviso tipo="erro">{mensagemDeErro(liberacoes.error)}</Aviso> : null}

      {liberacoes.data && (
        <div className="grid gap-1.5">
          {PAPEIS_DO_MEU_DINHEIRO.map(({ papel, rotulo }) => {
            const on = ligado(papel);
            const esteMudando = mudar.isPending && mudar.variables?.papel === papel;
            return (
              <button
                key={papel}
                type="button"
                role="switch"
                aria-checked={on}
                disabled={mudar.isPending}
                onClick={() => mudar.mutate({ papel, liberado: !on })}
                className="flex w-full items-center justify-between gap-3 border border-edge px-3 py-2.5 text-left disabled:opacity-60"
              >
                <span>
                  <span className="block text-sm font-bold">{rotulo}</span>
                  <span className={`block text-xs ${on ? 'text-ok' : 'text-dim'}`}>
                    {esteMudando ? 'mudando…' : on ? 'ligado' : 'desligado'}
                  </span>
                </span>
                <span aria-hidden="true"
                      className={`relative h-5 w-9 shrink-0 border ${on ? 'border-ok' : 'border-edge'}`}>
                  <span className={`absolute top-1/2 h-3 w-3 -translate-y-1/2 transition-[left] ${
                    on ? 'left-[1.1rem] bg-ok' : 'left-1 bg-dim'}`} />
                </span>
              </button>
            );
          })}
        </div>
      )}

      <p className="mt-2 text-xs text-dim">
        Você não vê quem usa nem o que tem dentro. Desligar não apaga nada.
      </p>

      {mudar.error ? <Aviso tipo="erro">{mensagemDeErro(mudar.error)}</Aviso> : null}
      {avisoNaoChegou && (
        <Aviso tipo="alerta">
          Gravado, mas o servidor não confirmou agora ({avisoNaoChegou}). Ele confere
          sozinho em até 5 minutos.
        </Aviso>
      )}
    </section>
  );
}

// --- Limites (migração 069) --------------------------------------------------
//
// Duas réguas, e a ordem importa na leitura: primeiro a da LOJA (o que vale
// para todo vendedor e todo gerente), depois a exceção nomeada, dentro do
// cartão de cada pessoa. Quem chega aqui para "dar mais desconto ao João"
// passa antes pela régua geral e vê o que está mudando — e, no caminho,
// descobre que existe a opção de mudar só para ele.
//
// As duas telas só existem para o dono. Quem faz valer é o banco: a RLS nega o
// gerente em limites_operacao e em limites_por_pessoa, e as RPCs recusam de
// novo lá dentro. Esconder aqui é para não oferecer um botão que erra.

function LimitesDaLoja({ limites }: { limites: LimitesOperacao }) {
  const cliente = useQueryClient();
  const [descVend, setDescVend] = useState(String(limites.desconto_max_pct_vendedor));
  const [descGer, setDescGer] = useState(String(limites.desconto_max_pct_gerente));
  const [piso, setPiso] = useState(String(limites.preco_min_pct_do_balcao));
  const [aVer, setAVer] = useState(String(limites.a_ver_max_gerente));
  const [abono, setAbono] = useState(String(limites.abono_sobra_max_vendedor));

  const gravar = useMutation({
    mutationFn: () => salvarLimitesDaLoja({
      descontoMaxPctVendedor: paraNumero(descVend) ?? 0,
      descontoMaxPctGerente: paraNumero(descGer) ?? 0,
      precoMinPctDoBalcao: paraNumero(piso) ?? 0,
      aVerMaxGerente: paraNumero(aVer) ?? 0,
      abonoSobraMaxVendedor: paraNumero(abono) ?? 0,
    }),
    onSuccess: () => {
      cliente.invalidateQueries({ queryKey: ['limites-operacao'] });
      cliente.invalidateQueries({ queryKey: ['limites-efetivos'] });
    },
  });

  return (
    <section className="cartao p-4">
      <h2 className="rotulo">Limites da loja</h2>
      <p className="mt-1 text-xs text-mute">
        A régua de todo mundo. Para abrir exceção a uma pessoa só, use
        “Limites desta conta” dentro do cartão dela — assim o resto da equipe
        não se mexe.
      </p>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <label className="rotulo" htmlFor="lim-desc-vend">Desconto máximo do vendedor (%)</label>
          <input id="lim-desc-vend" className="campo" inputMode="decimal"
                 value={descVend} onChange={(e) => setDescVend(e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor="lim-desc-ger">Desconto máximo do gerente (%)</label>
          <input id="lim-desc-ger" className="campo" inputMode="decimal"
                 value={descGer} onChange={(e) => setDescGer(e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor="lim-piso">Preço mínimo (% do preço da peça)</label>
          <input id="lim-piso" className="campo" inputMode="decimal"
                 value={piso} onChange={(e) => setPiso(e.target.value)} />
          <p className="mt-1 text-xs text-dim">
            Abaixo disso a venda é recusada. 70 significa “no mínimo 70% do preço
            de tabela”. O dono não tem piso.
          </p>
        </div>
        <div>
          <label className="rotulo" htmlFor="lim-aver">Vale “a ver” máximo do gerente (R$)</label>
          <input id="lim-aver" className="campo" inputMode="decimal"
                 value={aVer} onChange={(e) => setAVer(e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor="lim-abono">Sobra de vale que o vendedor pode abonar (R$)</label>
          <input id="lim-abono" className="campo" inputMode="decimal"
                 value={abono} onChange={(e) => setAbono(e.target.value)} />
        </div>
      </div>

      {gravar.error ? <Aviso tipo="erro">{mensagemDeErro(gravar.error)}</Aviso> : null}
      <button type="button" className="botao-secundario mt-3 w-full"
              disabled={gravar.isPending} onClick={() => gravar.mutate()}>
        {gravar.isPending ? 'Salvando…' : gravar.isSuccess ? 'Salvo ✓' : 'Salvar limites da loja'}
      </button>
    </section>
  );
}

function LimitesDaConta({
  perfil, ajuste, loja, aoMudar,
}: {
  perfil: Profile;
  ajuste: LimitesPorPessoa | undefined;
  loja: LimitesOperacao | undefined;
  aoMudar: () => void;
}) {
  const ehGerente = perfil.papel === 'gerente';

  const [desconto, setDesconto] = useState(String(ajuste?.desconto_max_pct ?? ''));
  const [piso, setPiso] = useState(String(ajuste?.preco_min_pct_do_balcao ?? ''));
  const [aVer, setAVer] = useState(String(ajuste?.a_ver_max ?? ''));
  const [abono, setAbono] = useState(String(ajuste?.abono_sobra_max ?? ''));
  const [motivo, setMotivo] = useState(ajuste?.observacao ?? '');

  // Mesma razão do efeito da remuneração acima: a lista de ajustes chega numa
  // requisição separada, e useState só lê o valor inicial. Sem isto, o cartão
  // aberto antes da resposta mostraria campos vazios — e salvar por cima
  // apagaria a folga já concedida.
  useEffect(() => {
    setDesconto(String(ajuste?.desconto_max_pct ?? ''));
    setPiso(String(ajuste?.preco_min_pct_do_balcao ?? ''));
    setAVer(String(ajuste?.a_ver_max ?? ''));
    setAbono(String(ajuste?.abono_sobra_max ?? ''));
    setMotivo(ajuste?.observacao ?? '');
  }, [ajuste?.desconto_max_pct, ajuste?.preco_min_pct_do_balcao,
      ajuste?.a_ver_max, ajuste?.abono_sobra_max, ajuste?.observacao]);

  const padraoDesconto = ehGerente
    ? loja?.desconto_max_pct_gerente : loja?.desconto_max_pct_vendedor;
  const padraoAbono = ehGerente ? null : loja?.abono_sobra_max_vendedor;

  const vazio = (t: string) => paraNumero(t);
  const temAjuste = vazio(desconto) !== null || vazio(piso) !== null
                 || vazio(aVer) !== null || vazio(abono) !== null;

  const gravar = useMutation({
    mutationFn: () => definirLimitesDaPessoa({
      profileId: perfil.id,
      descontoMaxPct: vazio(desconto),
      precoMinPctDoBalcao: vazio(piso),
      aVerMax: ehGerente ? vazio(aVer) : null,
      abonoSobraMax: vazio(abono),
      observacao: motivo.trim() || null,
    }),
    onSuccess: aoMudar,
  });

  // O placeholder diz o que o campo em branco significa. Enquanto a régua da
  // loja não chegou, "…" — dizer "sem teto" antes de saber seria a tela
  // afirmando exatamente o contrário do que costuma ser verdade.
  const dica = (padrao: number | null | undefined, sufixo: string) =>
    loja === undefined ? '…'
      : padrao == null ? 'sem teto'
      : `segue o papel: ${padrao}${sufixo}`;

  return (
    <div className="border border-edge p-3">
      <p className="rotulo">Limites desta conta</p>
      <p className="mt-1 text-xs text-mute">
        Campo em branco = segue o limite do papel. Preencher abre uma exceção
        só para {perfil.nome.split(' ')[0]} — e ela continua valendo quando você
        mudar a régua da loja.
      </p>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <label className="rotulo" htmlFor={`lp-desc-${perfil.id}`}>Desconto máximo (%)</label>
          <input id={`lp-desc-${perfil.id}`} className="campo" inputMode="decimal"
                 value={desconto} placeholder={dica(padraoDesconto, '%')}
                 onChange={(e) => setDesconto(e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor={`lp-piso-${perfil.id}`}>Preço mínimo (% da tabela)</label>
          <input id={`lp-piso-${perfil.id}`} className="campo" inputMode="decimal"
                 value={piso} placeholder={dica(loja?.preco_min_pct_do_balcao, '%')}
                 onChange={(e) => setPiso(e.target.value)} />
        </div>
        {/* Vale só aparece para gerente: vendedor não passa pelo exige_papel de
            emitir_a_ver, e a RPC recusa gravar um teto que nunca morderia. */}
        {ehGerente && (
          <div>
            <label className="rotulo" htmlFor={`lp-aver-${perfil.id}`}>Vale “a ver” máximo (R$)</label>
            <input id={`lp-aver-${perfil.id}`} className="campo" inputMode="decimal"
                   value={aVer} placeholder={dica(loja?.a_ver_max_gerente, '')}
                   onChange={(e) => setAVer(e.target.value)} />
          </div>
        )}
        <div>
          <label className="rotulo" htmlFor={`lp-abono-${perfil.id}`}>Sobra de vale que pode abonar (R$)</label>
          <input id={`lp-abono-${perfil.id}`} className="campo" inputMode="decimal"
                 value={abono} placeholder={dica(padraoAbono, '')}
                 onChange={(e) => setAbono(e.target.value)} />
        </div>
      </div>

      <div className="mt-3">
        <label className="rotulo" htmlFor={`lp-motivo-${perfil.id}`}>Motivo do ajuste</label>
        <input id={`lp-motivo-${perfil.id}`} className="campo" value={motivo}
               placeholder="por que esta conta sai da régua da loja"
               onChange={(e) => setMotivo(e.target.value)} />
        <p className="mt-1 text-xs text-dim">
          Fica registrado com seu nome. É o que explica o ajuste na auditoria
          seis meses depois.
        </p>
      </div>

      {gravar.error ? <Aviso tipo="erro">{mensagemDeErro(gravar.error)}</Aviso> : null}
      <button type="button" className="botao-secundario mt-3 w-full"
              disabled={gravar.isPending} onClick={() => gravar.mutate()}>
        {gravar.isPending ? 'Salvando…'
          : gravar.isSuccess ? 'Salvo ✓'
          : temAjuste ? 'Salvar limites desta conta'
          : 'Voltar aos limites do papel'}
      </button>

      {ajuste && (
        <p className="mt-2 text-xs text-dim">
          Ajuste em vigor desde {dataBR(ajuste.atualizado_em)}
          {ajuste.observacao ? ` — ${ajuste.observacao}` : ''}
        </p>
      )}
    </div>
  );
}

// --- Criação -----------------------------------------------------------------

function NovoFuncionario({
  aoFechar, aoCriar,
}: { aoFechar: () => void; aoCriar: () => void }) {
  const { papel: meuPapel } = useSessao();
  // Gerente só cria vendedor — a lista some para ele em vez de oferecer uma
  // opção que a Edge Function vai recusar.
  const papeisDisponiveis = PAPEIS.filter((p) => podeAdministrarPapel(meuPapel, p.id));

  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [papel, setPapel] = useState<Papel>('vendedor');
  const [telefone, setTelefone] = useState('');
  const [admitidoEm, setAdmitidoEm] = useState(hojeISO());

  const criar = useMutation({
    mutationFn: () => criarFuncionario({
      nome: nome.trim(),
      email: email.trim(),
      senha,
      papel,
      telefone: telefone.trim() || null,
      admitido_em: admitidoEm || null,
    }),
    onSuccess: aoCriar,
  });

  return (
    <section className="cartao space-y-4 border-signal/50 p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold">Novo funcionário</h2>
        <button type="button" onClick={aoFechar} className="px-2 text-sm text-dim">✕</button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="rotulo" htmlFor="f-nome">Nome *</label>
          <input id="f-nome" className="campo" value={nome} autoFocus
                 onChange={(e) => setNome(e.target.value)} placeholder="Maria Silva" />
        </div>
        <div>
          <label className="rotulo" htmlFor="f-email">E-mail de acesso *</label>
          <input id="f-email" type="email" className="campo" value={email} autoCapitalize="none"
                 onChange={(e) => setEmail(e.target.value)} placeholder="maria@serginho.local" />
        </div>
        <div>
          <label className="rotulo" htmlFor="f-senha">Senha provisória *</label>
          <input id="f-senha" type="text" className="campo font-mono" value={senha}
                 onChange={(e) => setSenha(e.target.value)} placeholder="mínimo 8 caracteres" />
          <p className="mt-1 text-xs text-dim">
            Combine com a pessoa no balcão.{' '}
            {podeAdministrar(meuPapel)
              ? 'Ela pode ser trocada aqui a qualquer momento.'
              : 'Depois, quem troca a senha de alguém é o dono.'}
          </p>
        </div>
        <div>
          <label className="rotulo" htmlFor="f-telefone">Telefone</label>
          <input id="f-telefone" className="campo" value={telefone}
                 onChange={(e) => setTelefone(e.target.value)} placeholder="(53) 99999-0000" />
        </div>
        <div>
          <label className="rotulo" htmlFor="f-admissao">Admissão</label>
          <input id="f-admissao" type="date" className="campo" value={admitidoEm}
                 onChange={(e) => setAdmitidoEm(e.target.value)} />
        </div>
      </div>

      <div>
        <p className="rotulo">Papel</p>
        <div className="grid gap-2 sm:grid-cols-3">
          {papeisDisponiveis.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setPapel(p.id)}
              aria-pressed={papel === p.id}
              className={[
                'border p-3 text-left',
                papel === p.id ? 'border-signal bg-signal/10' : 'border-edge',
              ].join(' ')}
            >
              <span className={`block text-sm font-bold ${papel === p.id ? 'text-signal-soft' : ''}`}>
                {p.rotulo}
              </span>
              <span className="mt-1 block text-xs text-mute">{p.resumo}</span>
            </button>
          ))}
        </div>
        {!podeAdministrar(meuPapel) && (
          <p className="mt-2 text-xs text-dim">
            Como gerente, você cadastra vendedores. Para criar outro gerente ou um dono, peça ao dono.
          </p>
        )}
      </div>

      {criar.error ? <Aviso tipo="erro">{mensagemDeErro(criar.error)}</Aviso> : null}

      <button type="button" className="botao-primario w-full" disabled={criar.isPending}
              onClick={() => criar.mutate()}>
        {criar.isPending ? 'Criando acesso…' : 'Criar acesso'}
      </button>
    </section>
  );
}

// --- A cor desta pessoa no mural (072) ---------------------------------------
//
// "Renan Azul": a cor é crachá, e é por isso que ela se define AQUI, no
// cadastro, e não no mural. Quem escolhe é quem administra a pessoa — a mesma
// hierarquia de sempre. O colaborador não se pinta sozinho: ele não atualiza a
// própria linha em profiles, e abrir esse update para resolver enfeite seria
// uma porta de escrita nova em cima da tabela que decide quem é quem.
//
// A tela mostra QUEM JÁ USA cada cor porque o banco não impede repetição — e
// não deve impedir: uma unicidade ali travaria o cadastro do sexto funcionário
// numa loja com oito cores, por causa de um enfeite. O aviso resolve o caso
// real (o dono não lembra que o azul já é do Renan) sem criar a trava.
function CorDoMural({ perfil, equipe, podeMudar, aoMudar }: {
  perfil: Profile;
  equipe: Profile[];
  podeMudar: boolean;
  aoMudar: () => void;
}) {
  const gravar = useMutation({
    mutationFn: (cor: CorDaEquipe) => salvarPerfil({ id: perfil.id, cor }),
    onSuccess: aoMudar,
  });

  const usadaPor = new Map<CorDaEquipe, string>();
  for (const p of equipe) {
    if (p.cor && p.id !== perfil.id && p.ativo) usadaPor.set(p.cor, p.nome);
  }
  const atual = corDe(perfil);

  return (
    <div className="border border-edge p-3">
      <p className="rotulo">Cor no mural de notas</p>
      <div className="flex flex-wrap gap-2">
        {CORES_DISPONIVEIS.map((cor) => {
          const { rotulo, hex } = CORES_DA_EQUIPE[cor];
          const dono = usadaPor.get(cor);
          const escolhida = perfil.cor === cor;
          return (
            <button
              key={cor}
              type="button"
              disabled={!podeMudar || gravar.isPending}
              aria-pressed={escolhida}
              aria-label={`${rotulo}${dono ? ` — em uso por ${dono}` : ''}`}
              title={dono ? `${rotulo} — em uso por ${dono}` : rotulo}
              onClick={() => gravar.mutate(cor)}
              className={`h-9 w-9 border-2 disabled:opacity-50 ${
                escolhida ? 'border-paper' : 'border-transparent'}`}
              style={{ backgroundColor: hex }}
            >
              {dono && <span aria-hidden="true" className="text-xs font-black text-ink">•</span>}
            </button>
          );
        })}
      </div>

      <p className="mt-2 text-xs text-dim">
        {perfil.cor === null ? (
          <>
            Ainda sem cor definida — no mural esta pessoa aparece em{' '}
            <span style={{ color: CORES_DA_EQUIPE[atual].hex }}>
              {CORES_DA_EQUIPE[atual].rotulo.toLowerCase()}
            </span>, uma cor provisória que não muda sozinha.
          </>
        ) : (
          <>
            No mural, as notas de {perfil.nome.split(' ')[0]} aparecem em{' '}
            <span style={{ color: CORES_DA_EQUIPE[atual].hex }}>
              {CORES_DA_EQUIPE[atual].rotulo.toLowerCase()}
            </span>. O ponto marca cor já usada por outra pessoa.
          </>
        )}
        {!podeMudar && ' Quem administra esta conta é que muda a cor.'}
      </p>

      {gravar.error ? <Aviso tipo="erro">{mensagemDeErro(gravar.error)}</Aviso> : null}
    </div>
  );
}

// --- Linha da equipe ---------------------------------------------------------

function Funcionario({
  perfil, termos, ajuste, limitesLoja, equipe, souEu, aberto, aoAbrir, aoMudar,
}: {
  perfil: Profile;
  termos: EmploymentTerms | undefined;
  ajuste: LimitesPorPessoa | undefined;
  limitesLoja: LimitesOperacao | undefined;
  /** A equipe inteira — só para dizer quem já usa cada cor do mural. */
  equipe: Profile[];
  souEu: boolean;
  aberto: boolean;
  aoAbrir: () => void;
  aoMudar: () => void;
}) {
  const { papel: meuPapel } = useSessao();
  const souDono = podeAdministrar(meuPapel);
  // Gerente abrindo a linha de um dono/gerente: vê os dados, não mexe em nada.
  const posso = podeAdministrarPapel(meuPapel, perfil.papel) && !souEu;

  const [salario, setSalario] = useState(String(termos?.salario_base ?? ''));
  const [comissao, setComissao] = useState(String(termos?.comissao_percent ?? ''));
  // 083: cada pessoa recebe no SEU dia, e o fluxo de caixa usa isso.
  const [diaPagamento, setDiaPagamento] = useState(String(termos?.dia_pagamento ?? 5));
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);

  // A lista de perfis e a de termos chegam em requisições separadas: este
  // componente monta antes dos termos existirem, e useState só lê o valor
  // inicial uma vez. Sem este efeito, o campo fica vazio para quem já tem
  // salário cadastrado — e salvar por cima zeraria o valor.
  // Depende dos VALORES, não do objeto: digitar altera só o estado local.
  useEffect(() => {
    setSalario(String(termos?.salario_base ?? ''));
    setComissao(String(termos?.comissao_percent ?? ''));
  }, [termos?.salario_base, termos?.comissao_percent]);

  const mudarPapel = useMutation({
    mutationFn: (papel: Papel) => salvarPerfil({ id: perfil.id, papel }),
    onSuccess: aoMudar,
  });

  // Desligar corta o acesso DE VERDADE (bane a conta no Auth), não só marca o
  // perfil — ver alternarDesligamento em consultas.ts.
  const alternarAtivo = useMutation({
    mutationFn: () => alternarDesligamento(perfil.id, perfil.ativo),
    onSuccess: aoMudar,
  });

  const gravarTermos = useMutation({
    mutationFn: () => salvarTermos({
      profile_id: perfil.id,
      salario_base: paraNumero(salario) ?? 0,
      comissao_percent: paraNumero(comissao) ?? 0,
      dia_pagamento: Math.min(Math.max(Math.trunc(paraNumero(diaPagamento) ?? 5), 1), 31),
    }),
    onSuccess: aoMudar,
  });

  const trocarSenha = useMutation({
    mutationFn: () => redefinirSenha(perfil.id, novaSenha),
    onSuccess: () => setNovaSenha(''),
  });

  const excluir = useMutation({
    mutationFn: () => excluirFuncionario(perfil.id),
    onSuccess: aoMudar,
  });

  return (
    <article className={`cartao ${perfil.ativo ? '' : 'opacity-60'}`}>
      <button type="button" onClick={aoAbrir} className="flex w-full items-center justify-between gap-3 p-4 text-left">
        <div className="min-w-0">
          <p className="truncate font-bold">
            {perfil.nome}
            {souEu && <span className="ml-2 text-xs font-normal text-dim">(você)</span>}
          </p>
          <p className="truncate text-xs text-mute">{perfil.email ?? '—'}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="font-mono text-[0.625rem] uppercase tracking-wider text-signal-soft">
            {perfil.papel}
          </span>
          <span className="text-dim">{aberto ? '▲' : '▼'}</span>
        </div>
      </button>

      {aberto && (
        <div className="space-y-4 border-t border-edge p-4">
          <div className="grid gap-3 text-xs text-mute sm:grid-cols-2">
            <p>Admissão: <span className="text-paper">{dataBR(perfil.admitido_em)}</span></p>
            {perfil.telefone && <p>Telefone: <span className="text-paper">{perfil.telefone}</span></p>}
            {perfil.desligado_em && (
              <p>Desligamento: <span className="text-alerta">{dataBR(perfil.desligado_em)}</span></p>
            )}
          </div>

          {!posso && !souEu && (
            <Aviso tipo="info">
              Você pode ver este cadastro, mas quem administra {perfil.papel} é o dono.
            </Aviso>
          )}

          <div>
            <label className="rotulo" htmlFor={`papel-${perfil.id}`}>Papel</label>
            <select
              id={`papel-${perfil.id}`}
              className="campo"
              value={perfil.papel}
              // Trocar papel é privilégio do dono: se o gerente pudesse promover
              // alguém a dono, teria criado um dono por via indireta.
              disabled={souEu || !souDono}
              onChange={(e) => mudarPapel.mutate(e.target.value as Papel)}
            >
              {PAPEIS.map((p) => <option key={p.id} value={p.id}>{p.rotulo}</option>)}
            </select>
            {souEu ? (
              <p className="mt-1 text-xs text-dim">
                Você não altera o próprio papel — evita a loja ficar sem nenhum dono.
              </p>
            ) : !souDono && (
              <p className="mt-1 text-xs text-dim">Só o dono muda papel.</p>
            )}
          </div>

          {/* A cor do mural (072). A régua é a MESMA da policy upd_profiles —
              dono pinta qualquer um (inclusive a si), gerente só vendedor —, e
              por isso ela sai de podeAdministrarPapel e não de um `souDono`
              solto: duas escritas da mesma regra é como a tela e o banco
              passam a discordar. */}
          <CorDoMural
            perfil={perfil}
            equipe={equipe}
            podeMudar={podeAdministrarPapel(meuPapel, perfil.papel)}
            aoMudar={aoMudar}
          />

          {/* Dinheiro de pessoa: só existe nesta tela, só para o dono. A RLS de
              employment_terms já nega o gerente — esconder evita um erro seco. */}
          {souDono && (
          <div className="border border-edge p-3">
            <p className="rotulo">Remuneração</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="rotulo" htmlFor={`sal-${perfil.id}`}>Salário base (R$)</label>
                <input id={`sal-${perfil.id}`} className="campo" inputMode="decimal"
                       value={salario} placeholder="0,00"
                       onChange={(e) => setSalario(e.target.value)} />
              </div>
              <div>
                <label className="rotulo" htmlFor={`com-${perfil.id}`}>Comissão (% do vendido)</label>
                <input id={`com-${perfil.id}`} className="campo" inputMode="decimal"
                       value={comissao} placeholder="0"
                       onChange={(e) => setComissao(e.target.value)} />
              </div>
              <div>
                <label className="rotulo" htmlFor={`dia-${perfil.id}`}>Recebe no dia</label>
                <input id={`dia-${perfil.id}`} className="campo" inputMode="numeric"
                       value={diaPagamento} placeholder="5"
                       onChange={(e) => setDiaPagamento(e.target.value)} />
              </div>
            </div>
            <p className="mt-2 text-xs text-dim">
              A comissão é sugerida automaticamente no fechamento da folha, calculada sobre o que
              esta pessoa vendeu no mês (venda cancelada não conta). O <strong>dia</strong> é
              quando esta pessoa recebe: é ele que põe a folha no dia certo do fluxo de caixa.
              Mês curto encurta junto — dia 31 cai no último dia de fevereiro.
            </p>
            {gravarTermos.error ? <Aviso tipo="erro">{mensagemDeErro(gravarTermos.error)}</Aviso> : null}
            <button type="button" className="botao-secundario mt-3 w-full"
                    disabled={gravarTermos.isPending} onClick={() => gravarTermos.mutate()}>
              {gravarTermos.isPending ? 'Salvando…' : gravarTermos.isSuccess ? 'Salvo ✓' : 'Salvar remuneração'}
            </button>
          </div>
          )}

          {/* O teto desta conta (069). Não aparece para alvo DONO porque dono é
              isento por desenho — e definir_limites_da_pessoa recusaria, então
              o campo seria um botão que só sabe errar. */}
          {souDono && perfil.papel !== 'dono' && (
            <LimitesDaConta perfil={perfil} ajuste={ajuste} loja={limitesLoja} aoMudar={aoMudar} />
          )}

          {/* Trocar a senha de alguém é assumir a conta dele — e, desde o Meu
              dinheiro (ADR-008, decisão 9 do dono em 18/09/2026), é também a
              porta para o planejamento pessoal dessa pessoa. Por isso só o DONO
              troca a senha do sistema de alguém; o agente da VPS recusa o
              gerente do outro lado, e esconder aqui evita um botão que só erra. */}
          {posso && !souDono && (
            <p className="text-xs text-dim">
              Trocar a senha de alguém é só com o dono.
            </p>
          )}
          {posso && souDono && (
          <div className="border border-edge p-3">
            <label className="rotulo" htmlFor={`senha-${perfil.id}`}>Trocar senha</label>
            <div className="flex gap-2">
              <input id={`senha-${perfil.id}`} className="campo font-mono" value={novaSenha}
                     placeholder="nova senha (8+)"
                     onChange={(e) => setNovaSenha(e.target.value)} />
              <button type="button" className="botao-secundario shrink-0"
                      disabled={trocarSenha.isPending || novaSenha.length < 8}
                      onClick={() => trocarSenha.mutate()}>
                {trocarSenha.isPending ? '…' : 'Trocar'}
              </button>
            </div>
            {trocarSenha.error ? <Aviso tipo="erro">{mensagemDeErro(trocarSenha.error)}</Aviso> : null}
            {trocarSenha.isSuccess && <Aviso tipo="ok">Senha trocada.</Aviso>}
          </div>
          )}

          <div className="flex flex-wrap gap-2">
            {posso && (
            <button
              type="button"
              className="botao-secundario flex-1"
              disabled={alternarAtivo.isPending}
              onClick={() => alternarAtivo.mutate()}
            >
              {perfil.ativo ? 'Desligar' : 'Reativar'}
            </button>
            )}

            {!souDono || souEu ? null : !confirmandoExclusao ? (
              <button type="button" className="border border-edge px-4 py-3 text-sm font-bold text-erro"
                      onClick={() => setConfirmandoExclusao(true)}>
                Excluir
              </button>
            ) : (
              <div className="w-full space-y-2 border border-erro/50 bg-erro/10 p-3">
                <p className="text-sm text-erro">
                  Excluir apaga o acesso e o cadastro de <strong>{perfil.nome}</strong>. As vendas
                  feitas por ela continuam no sistema, com o nome preservado. Não dá para desfazer.
                </p>
                <p className="text-xs text-mute">
                  Para quem saiu da empresa, o certo é <strong>Desligar</strong>: corta o acesso na
                  hora e mantém tudo.
                </p>
                <div className="flex gap-2">
                  <button type="button" className="botao-secundario flex-1"
                          onClick={() => setConfirmandoExclusao(false)}>
                    Cancelar
                  </button>
                  <button type="button"
                          className="botao-perigo flex-1 px-4 py-3 text-sm"
                          disabled={excluir.isPending} onClick={() => excluir.mutate()}>
                    {excluir.isPending ? 'Excluindo…' : 'Excluir mesmo assim'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {(mudarPapel.error || alternarAtivo.error || excluir.error) && (
            <Aviso tipo="erro">
              {mensagemDeErro(mudarPapel.error ?? alternarAtivo.error ?? excluir.error)}
            </Aviso>
          )}

          {termos && (
            <p className="text-xs text-dim">
              Remuneração atual: {brl(termos.salario_base)}
              {termos.comissao_percent > 0 && ` + ${termos.comissao_percent}% de comissão`}
            </p>
          )}
        </div>
      )}
    </article>
  );
}
